import {
  Box,
  Button,
  Flex,
  LoadingOverlay,
  Modal,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createDevice } from '../services/deviceManager';

type DeviceType = 'vr-headset' | 'smartwatch';

export default function CreateDeviceModal({
  opened,
  close,
  callback,
  defaultType,
  userId,
}: {
  opened: boolean;
  close: () => void;
  callback: (json: any) => void;
  defaultType?: DeviceType | null;
  userId?: string;
}) {
  const { t } = useTranslation();
  const [inProgress, setInProgress] = useState<boolean>(false);

  const form = useForm({
    mode: 'uncontrolled',
    initialValues: {
      name: '',
      type: defaultType ?? '',
      model: '',
    },
  });

  // Aggiorna "type" quando apro/cambia defaultType e reset al close
  useEffect(() => {
    if (opened) {
      form.setValues({
        type: defaultType ?? '',
      });
    }
    return () => form.reset();
  }, [opened, defaultType]);

  // Opzioni modello in base al tipo selezionato
  const modelOptions = useMemo(() => {
    const currentType: DeviceType | '' =
      (form.getValues().type as DeviceType) || defaultType || '';
    if (currentType === 'vr-headset') {
      return [
        { value: 'Meta Quest 2', label: 'Meta Quest 2' },
        { value: 'Meta Quest 3', label: 'Meta Quest 3' },
        { value: 'Meta Quest 3s', label: 'Meta Quest 3s' },
        { value: 'Meta Quest Pro', label: 'Meta Quest Pro' },
      ];
    }
    if (currentType === 'smartwatch') {
      return [
        { value: 'Galaxy Watch7', label: 'Galaxy Watch7' },
        // TODO: aggiungere i modelli precedenti
        // { value: 'Galaxy Watch6', label: 'Galaxy Watch6' },
        // { value: 'Galaxy Watch5', label: 'Galaxy Watch5' },
        // { value: 'Galaxy Watch4', label: 'Galaxy Watch4' },
        // { value: 'Pixel Watch 2', label: 'Pixel Watch 2' },
        // { value: 'Pixel Watch', label: 'Pixel Watch' },
        // { value: 'Wear OS (Generic)', label: 'Wear OS (Generic)' },
        { value: 'Polar 360', label: 'Polar 360' },
      ];
    }
    return [];
  }, [form, defaultType]);

  // Se cambia il tipo (quando la Select è visibile), svuoto il modello
  const handleTypeChange = (val: string | null) => {
    form.setFieldValue('type', val ?? '');
    form.setFieldValue('model', ''); // reset modello
  };

  const submit = async (values: any) => {
    if (!values.type) {
      notifications.show({
        color: 'red',
        withBorder: true,
        radius: 'lg',
        title: 'Tipo mancante',
        autoClose: 3000,
        message: 'Seleziona o imposta il tipo di dispositivo.',
      });
      return;
    }
    if (!values.model) {
      notifications.show({
        color: 'red',
        withBorder: true,
        radius: 'lg',
        title: 'Modello mancante',
        autoClose: 3000,
        message: 'Seleziona il modello del dispositivo.',
      });
      return;
    }

    setInProgress(true);

    try {
      await createDevice(values, userId);
      callback({ success: true });
      notifications.show({
        color: 'teal',
        withBorder: true,
        radius: 'lg',
        title: 'Dispositivo Creato',
        autoClose: 2000,
        message: 'Il dispositivo è stato creato con successo',
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      console.error('error', error, errorMessage);
      notifications.show({
        color: 'red',
        withBorder: true,
        radius: 'lg',
        title: 'An error occurred',
        autoClose: false,
        message: errorMessage,
      });
    } finally {
      setInProgress(false);
    }
  };

  const deviceLabel =
    defaultType === 'vr-headset'
      ? 'Visore di Realtà Virtuale'
      : defaultType === 'smartwatch'
        ? 'Sensore'
        : 'Info';

  return (
    <Modal opened={opened} onClose={close} title={deviceLabel} size='lg'>
      <form onSubmit={form.onSubmit(values => submit(values))}>
        <Box pos='relative'>
          <LoadingOverlay
            visible={inProgress}
            zIndex={1000}
            overlayProps={{ radius: 'sm', blur: 2 }}
          />
          <Stack>
            <TextInput
              {...form.getInputProps('name')}
              label={t(
                'ccm_device',
                'Dai un nome unico al tuo dispositivo per distinguerlo'
              )}
              placeholder={t(
                'ccm_device_p',
                'Inserisci un nome per il tuo dispositivo'
              )}
            />

            {/* Tipo: nascosto se imposto dal padre, altrimenti Select utente */}
            {defaultType ? (
              <Text size='sm'>
                Tipo: <b>{deviceLabel}</b>
              </Text>
            ) : (
              <Select
                data={[
                  {
                    value: 'vr-headset',
                    label: 'Visore di Realtà Virtuale (Meta quest)',
                  },
                  {
                    value: 'smartwatch',
                    label: 'Sensore (Fitbit, Galaxy, Polar)',
                  },
                ]}
                value={form.getValues().type || ''}
                onChange={handleTypeChange}
                label={t('ccm_type', 'Tipo di dispositivo')}
                required
                searchable
                clearable={false}
              />
            )}

            {/* Modello (dipende dal tipo) */}
            <Select
              data={modelOptions}
              {...form.getInputProps('model')}
              value={form.getValues().model}
              onChange={v => form.setFieldValue('model', v ?? '')}
              label={t('ccm_model', 'Modello')}
              placeholder={t('ccm_model_p', 'Seleziona un modello')}
              required
              searchable
              nothingFoundMessage={t('nothing_found', 'Nessun risultato')}
              disabled={modelOptions.length === 0}
            />
          </Stack>
        </Box>

        <Flex mt='md' justify='flex-end'>
          <Button type='submit' disabled={inProgress} loading={inProgress}>
            {t('create', 'Crea')}
          </Button>
        </Flex>
      </form>
    </Modal>
  );
}
